<template>
  <div id="app">
    <b-table
      responsive
      hover
      :striped="striped"
      :bordered="bordered"
      :borderless="borderless"
      :outlined="outlined"
      :small="small"
      :dark="dark"
      :fixed="fixed"
      :foot-clone="footClone"
      :no-border-collapse="noCollapse"
      :items="items"
      :fields="fields"
      :head-variant="headVariant"
      :table-variant="tableVariant"
      class="text-xs-left table-no-margin-top w-100"
      show-empty
    >
      <template
          v-for="slot in Object.keys($scopedSlots)"
          v-slot:
          [toCellName(slot)]="props"
      >
        <slot v-bind="props" :name="slot"/>
      </template>
      <template #head(meanPosition)="scope">
        <table-header-with-tooltip
          label="Средняя позиция"
          tooltip-text="Среднее значение позиций объявлений среди всех показов в результатах поиска. Обычно в верхнем блоке рекламы отображается до 4 объявлений, все остальные отображаются в нижнем блоке."
        />
      </template>
      <template #head(special_share)="scope">
        <table-header-with-tooltip
          label="Доля премиум-показов"
          tooltip-text="Доля страниц, где объявления были в верхнем рекламном блоке над органикой."
        />
      </template>
      <template #head(sov)="scope">
        <table-header-with-tooltip
          label="SOV"
          tooltip-text="Доля страниц, на которых объявления были показаны в рекламных блоках (как в верхнем, так и в нижнем)."
        />
      </template>
      <template #head(resultsInTop)="scope">
        <table-header-with-tooltip
          label="Доля премиум-показов"
          tooltip-text="Доля страниц, где объявления были в верхнем рекламном блоке над органикой."
        />
      </template>
      <template #head(pct_in_title)="scope">
        Вхождение ключа<br>в заголовок
      </template>
      <template #head(pct_in_content)="scope">
        Вхождение ключа<br>в текст
      </template>
      <template #head(pctInTitle1)="scope">
        Вхождение ключа<br>в заголовок
      </template>
      <template #head(pctInContent1)="scope">
        Вхождение ключа<br>в текст
      </template>
      <template #head(pctInTitle2)="scope">
        Вхождение ключа<br>в заголовок
      </template>
      <template #head(pctInContent2)="scope">
        Вхождение ключа<br>в текст
      </template>
      <template #cell(advertiser)="data">
        <div :style="getColumnStyles(data)" class="sov-cell-wrapper d-flex align-items-center">
          <template v-if="isAdvertiserWithReportLink(data.item)">
            <a
              href="#"
              class="advertiser-report-link ml-1"
              @click.prevent="onDownloadAdvertiserReport && onDownloadAdvertiserReport(data.item.advertiser_id, data.item.advertiser)"
            >{{ data.value }}</a>
            <tooltip
              text="Нажмите на гиперссылку, для скачивания отчета по входящим в рекламодателя ссылкам / доменам"
              class="advertiser-report-tooltip"
            >
              <i class="fas fa-info-circle" style="font-size: 12px; color: rgb(108, 117, 125); margin-top: 5px;"></i>
            </tooltip>
          </template>
          <template v-else>
            <template v-if="comparing && isMetricWithDiff(data.field.key)">
              <span class="sov-cell-main">{{ data.value }}</span><sup class="sov-cell-diff" :class="hasDiff(data) ? diffColorClass(data) : 'sov-diff-zero'">{{ hasDiff(data) ? formatDiff(data) : (data.field.key === 'meanPosition' ? '0,00' : '0%') }}</sup>
            </template>
            <template v-else>
              {{ data.value }}
            </template>
          </template>
        </div>
      </template>
      <template #cell()="data">
        <div :style="getColumnStyles(data)" class="sov-cell-wrapper">
          <template v-if="comparing && isMetricWithDiff(data.field.key)">
            <span class="sov-cell-main">{{ data.value }}</span><sup class="sov-cell-diff" :class="hasDiff(data) ? diffColorClass(data) : 'sov-diff-zero'">{{ hasDiff(data) ? formatDiff(data) : (data.field.key === 'meanPosition' ? '0,00' : '0%') }}</sup>
          </template>
          <template v-else>
            {{ data.value }}
          </template>
        </div>
      </template>
      <template #empty="scope" style="text-align: center;">
        За выбранный период данные отсутствуют
      </template>
    </b-table>
  </div>
</template>

<script>
import EventHubListenerMixin from "../../common/mixins/EventHubListener";
import TableHeaderWithTooltip from "../../common/TableHeaderWithTooltip";
import Tooltip from "../../common/Tooltip";

const ADVERTISER_IDS_WITH_REPORT_LINK = [27274, 27984];

export default {
  name: "ResultsTable",
  components: { EventHubListenerMixin, TableHeaderWithTooltip, Tooltip },
  props: {
    fields: {
      type: Array,
      default: [],
    },
    items: {
      type: Array,
      default: [],
    },
    comparing: {
      type: Boolean,
      default: false,
    },
    /** Функция (advertiserId, advertiserTitle) для скачивания отчёта по ссылкам/доменам */
    onDownloadAdvertiserReport: {
      type: Function,
      default: null,
    },
  },

  data: function () {
    return {
      loading: true,
      striped: false,
      bordered: false,
      borderless: false,
      outlined: false,
      small: false,
      dark: false,
      fixed: false,
      footClone: false,
      headVariant: null,
      tableVariant: "",
      noCollapse: false,
    };
  },

  methods: {
    toCellName(slot) {
      return `cell(${slot})`;
    },

    isAdvertiserWithReportLink(item) {
      const id = item && item.advertiser_id;
      return id != null && ADVERTISER_IDS_WITH_REPORT_LINK.indexOf(id) !== -1;
    },

    getColumnStyles(data) {
      const textAlign = ['advertiser', 'keyword', 'date'].includes(data.field.key) ? 'left' : 'center';
      return { textAlign };
    },

    isMetricWithDiff(key) {
      return ['sov', 'meanPosition', 'special_share', 'pct_in_title', 'pct_in_content'].includes(key);
    },

    getDiffKey(key) {
      const map = { sov: 'sov_diff', meanPosition: 'mean_position_diff', special_share: 'special_share_diff', pct_in_title: 'pct_in_title_diff', pct_in_content: 'pct_in_content_diff' };
      return map[key];
    },

    hasDiff(data) {
      const diffKey = this.getDiffKey(data.field.key);
      const diff = data.item[diffKey];
      return diff !== undefined && diff !== null && !Number.isNaN(diff);
    },

    isPercentMetric(key) {
      return ['sov', 'special_share', 'pct_in_title', 'pct_in_content'].includes(key);
    },

    formatDiff(data) {
      const diffKey = this.getDiffKey(data.field.key);
      const diff = data.item[diffKey];
      if (diff === undefined || diff === null || Number.isNaN(diff)) return '';
      const isMeanPosition = data.field.key === 'meanPosition';
      const pctSuffix = this.isPercentMetric(data.field.key) ? '%' : '';
      const num = Number(diff);
      const displayVal = isMeanPosition ? Math.abs(num) : Math.round(num);
      const isDisplayZero = (isMeanPosition && displayVal < 0.005) || (!isMeanPosition && displayVal === 0);
      if (isDisplayZero) {
        return (isMeanPosition ? '0,00' : '0') + pctSuffix;
      }
      const sign = isMeanPosition
        ? (num > 0 ? '+' : (num < 0 ? '−' : ''))
        : (num > 0 ? '+' : '');
      const str = isMeanPosition ? Math.abs(num).toFixed(2).replace('.', ',') : String(Math.round(num));
      return sign + str + pctSuffix;
    },

    diffColorClass(data) {
      const diffKey = this.getDiffKey(data.field.key);
      const diff = data.item[diffKey];
      if (diff === undefined || diff === null || Number.isNaN(diff)) return '';
      const num = Number(diff);
      const isMeanPosition = data.field.key === 'meanPosition';
      const displayVal = isMeanPosition ? Math.abs(num) : Math.round(num);
      const isDisplayZero = (isMeanPosition && displayVal < 0.005) || (!isMeanPosition && displayVal === 0);
      if (isDisplayZero) return 'sov-diff-zero';
      if (isMeanPosition) return num > 0 ? 'sov-diff-red' : 'sov-diff-green';
      return num > 0 ? 'sov-diff-green' : 'sov-diff-red';
    },
  },
};
</script>

<style scoped>
.sov-cell-main {
  font-weight: 700;
}
.sov-cell-diff {
  font-size: 0.75em;
  font-weight: 100;
  margin-left: 2px;
}
.sov-diff-green {
  color: #198754;
}
.sov-diff-red {
  color: #dc3545;
}
.sov-diff-zero {
  color: #6c757d;
}

.advertiser-report-tooltip {
  margin-left: 4px;
}

.advertiser-report-link {
  color: #0d6efd;
  text-decoration: underline;
}
</style>
